/**
 * Format markup data for ez-bill table display
 * Similar to portal's formatData2 function
 */

import { getTestTree } from "@/utils/va-order-test/testTree/testTree";
import { bfsSearch } from "@/utils/va-order-test/testTree/bfsSearch";

// VW non-test IDs
const vwNonTests = [725, 726, 731, 800, 801, 802, 803];

// Package panel IDs that contain sub-tests to exclude
const packagePanelIds = [98, 99, 727];

/**
 * Calculate marked up price based on markup type
 * @param {number} wholeSalePrice - base wholesale price
 * @param {string} markUpType - markup type: no_markup, added_percentage, fixed_profit, fixed_price
 * @param {number} markUpValue - markup value
 * @returns {number} marked up price
 */
export function calculateMarkedUpPrice(wholeSalePrice, markUpType, markUpValue) {
  switch (markUpType) {
    case 'no_markup':
      return wholeSalePrice;
    case 'added_percentage':
      return (markUpValue + 1) * wholeSalePrice;
    case 'fixed_profit':
      return markUpValue + wholeSalePrice;
    case 'fixed_price':
      return markUpValue;
    default:
      return wholeSalePrice;
  }
}

/**
 * Build duplicate ID set from VA tree nodes - excludes sub-tests of package panels
 * Uses tree nodes (after BFS traversal) like Portal does
 * @param {Array} vaNodeList - VA tree nodes from BFS traversal
 * @returns {Set} Set of IDs to exclude
 */
function buildDuplicateIdSet(vaNodeList) {
  const excludeSet = new Set();
  if (!vaNodeList || !Array.isArray(vaNodeList)) {
    return excludeSet;
  }

  // Find the un-orderable tests in Comprehensive Metabolic Panel (98),
  // Basic Metabolic Panel (99), and Food Allergen Panel (727)
  // They should not appear in the markup table
  vaNodeList.forEach(node => {
    if (packagePanelIds.includes(node.id) && node.value.packageTests && Array.isArray(node.value.packageTests)) {
      node.value.packageTests.forEach(subTest => {
        excludeSet.add(subTest.id);
      });
    }
  });

  return excludeSet;
}

/**
 * Get base name without "(at home)" suffix
 * @param {string} name - test name
 * @returns {string} base name
 */
function getBaseName(name) {
  if (!name) return '';
  return name.replace(/\s*\(at home\)$/i, '').trim();
}

/**
 * Check if test name is Tickborne (for special DBS/Serum handling)
 * @param {string} name - test name
 * @returns {boolean}
 */
function isTickborneTest(name) {
  return /\bTickborne\b.*\b(1\.0|2\.0)\b/i.test(name || "");
}

/**
 * Find parents of a tree node to build category hierarchy
 * @param {TreeNode} node - The tree node
 * @returns {Array} Array of parent test names
 */
function findParents(node) {
  const testNames = node.value && node.value.testName ? [node.value.testName] : [];
  if (node.level !== 0 && node.parent) {
    const parentTestNames = findParents(node.parent);
    return parentTestNames.concat(testNames);
  }
  return testNames;
}

/**
 * Get category string from parents
 * @param {Array} parents - Array of parent names
 * @returns {string} Category string
 */
function getCategory(parents) {
  return parents.length <= 1 ? "" : parents.slice(0, -1).join(", ");
}

/**
 * Format all data from APIs for ez-bill table (similar to Portal's formatData2)
 * Updated to use tree structure for category hierarchy
 * @param {Array} vaTests - VA tests from getAllTestsAndDiscountPanels
 * @param {Array} vwTests - VW tests from getAllTestsAndDiscountPanels
 * @param {Array} promotions - promotions from getPromotions
 * @param {Array} customPanels - custom panels from getCustomPanels
 * @param {Array} markupSchemes - markup schemes from getMarkUps
 * @param {Array} discountPanels - discount panels from Vuex store
 * @returns {Object} object with vaData, vwData, promotionData, discountData, customData
 */
export function formatData2(vaTests, vwTests, promotions, customPanels, markupSchemes = [], discountPanels = [], vwCategories = []) {
  // Create a map for quick lookup of markup schemes
  const schemeMap = {};
  if (markupSchemes && Array.isArray(markupSchemes)) {
    markupSchemes.forEach(scheme => {
      const key = `${scheme.item_type}-${scheme.item_id}`;
      schemeMap[key] = scheme;
    });
  }

  // Set category on raw tests BEFORE building tree (same as VA Portal)
  (vaTests || []).forEach(test => { test.category = 'va'; });
  (vwTests || []).forEach(test => { test.category = 'vw'; });

  // Build VA tree structure
  const vaTree = getTestTree(vaTests || []);
  const vaNodeList = [];
  vaTree.forEach(root => bfsSearch(root, vaNodeList, 0));

  // Build VW tree structure
  const vwTree = getTestTree(vwTests || []);
  const vwNodeList = [];
  vwTree.forEach(root => bfsSearch(root, vwNodeList, 0));

  // Build duplicate ID set from VA tree nodes (excludes sub-tests of package panels)
  const duplicateIdSet = buildDuplicateIdSet(vaNodeList);

  // Helper to format a single item
  function formatItem(item, type, index = 0) {
    // Determine item_id based on type
    let itemType, itemId;
    switch (type) {
      case 'va':
      case 'vw':
        itemType = 'packagePriceId';
        itemId = item.id || item.testId;
        break;
      case 'promotion':
        itemType = 'oldPromotionId';
        itemId = item.id || item.promotionId;
        break;
      case 'discount':
      case 'custom':
        itemType = 'bundleId';
        itemId = item.id || item.bundleId;
        break;
      default:
        itemType = 'packagePriceId';
        itemId = item.id;
    }

    // Get markup scheme
    const schemeKey = `${itemType}-${itemId}`;
    const scheme = schemeMap[schemeKey] || {};

    const markUpType = scheme.markup_type || 'no_markup';
    const markUpValue = scheme.markup_value || 0;
    const wholeSalePrice = item.price || 0;
    const markedUpPrice = calculateMarkedUpPrice(wholeSalePrice, markUpType, markUpValue);
    const profit = markedUpPrice - wholeSalePrice;

    return {
      id: itemId,
      index,
      type,
      name: item.name || item.testName || item.displayName || item.discountName || item.bundleName || '',
      category: item.category || '',
      nameShorten: '',
      categoryShorten: '',
      wholeSalePrice,
      markUpType,
      markUpValue,
      markedUpPrice,
      profit,
      changable: scheme.changeable !== false,
      customized_name: scheme.customized_name || '',
      activated: true,
      isTest: true,
      selected: false
    };
  }

  // Format VA tests using tree nodes for category hierarchy
  const vaData = [];
  vaNodeList.forEach((node) => {
    const item = node.value;
    // Skip if this is a sub-test of a package panel
    if (duplicateIdSet.has(item.id)) {
      return;
    }

    // Handle at_home suffix (Portal: displayName || testName)
    let name = item.displayName ? item.displayName : item.testName || '';
    if (item.section === "at_home") {
      name = name + "(at home)";
    }

    // Get category from tree hierarchy
    const parents = findParents(node);
    const category = getCategory(parents);

    const formatted = formatItem({ ...item, name, category }, 'va', vaData.length);
    formatted.nameShorten = name.length <= 23 ? name : name.substring(0, 21) + " ...";
    formatted.categoryShorten = category.length <= 23 ? category : category.substring(0, 21) + " ...";
    vaData.push(formatted);
  });

  // VP-16301: only include VW tests exposed by backend vwCategories
  // (source of truth for orderable VW items; discontinued tests like
  // Connective Tissue Panel won't appear in the eZ-Bill adjustment list)
  const vwCategoryIdSet = new Set(
    (vwCategories || []).flatMap(c => c.contents || [])
  );
  const vwNodeListFiltered = vwCategoryIdSet.size > 0
    ? vwNodeList.filter(node => vwCategoryIdSet.has(node.id))
    : vwNodeList;

  // Format VW tests using tree nodes for category hierarchy
  const vwRawData = [];
  vwNodeListFiltered.forEach((node) => {
    const item = node.value;
    // Skip offline tests
    if (item.toBeOffline === true) {
      return;
    }

    // Handle at_home suffix (Portal: displayName || testName)
    let name = item.displayName ? item.displayName : item.testName || '';

    // Handle Tickborne special naming
    if (isTickborneTest(name)) {
      if (item.section === "at_home") {
        name = `${name} (DBS)`;
      } else {
        name = `${name} (Serum)`;
      }
    } else {
      // Normal at-home handling
      if (item.section === "at_home") {
        name = `${name} (at home)`;
      }
    }

    // Get category from tree hierarchy
    const parents = findParents(node);
    const category = getCategory(parents);

    const formatted = formatItem({ ...item, name, category }, 'vw', vwRawData.length);
    formatted.isTest = !vwNonTests.includes(item.id);
    formatted.nameShorten = name.length <= 23 ? name : name.substring(0, 21) + " ...";
    formatted.categoryShorten = category.length <= 23 ? category : category.substring(0, 21) + " ...";
    vwRawData.push(formatted);
  });

  // Deduplicate VW data by base name + price
  const vwData = [];
  const vwDedupeMap = new Map();
  vwRawData.forEach(item => {
    const baseName = getBaseName(item.name);
    const key = `${baseName}::${item.wholeSalePrice}`;
    if (!vwDedupeMap.has(key)) {
      // First occurrence - use base name and store IDs array
      const copy = { ...item, name: baseName, ids: [item.id] };
      copy.nameShorten = baseName.length <= 23 ? baseName : baseName.substring(0, 21) + " ...";
      vwDedupeMap.set(key, copy);
    } else {
      // Duplicate - collect IDs
      const existing = vwDedupeMap.get(key);
      if (!existing.ids.includes(item.id)) {
        existing.ids.push(item.id);
      }
    }
  });
  // Convert map to array and update indices
  Array.from(vwDedupeMap.values()).forEach((item, idx) => {
    item.index = idx;
    vwData.push(item);
  });

  // Format promotions - exclude specific bundle
  const promotionData = [];
  if (promotions && Array.isArray(promotions)) {
    promotions.forEach((item) => {
      const name = item.discountName || item.name || '';
      // Exclude "3 for $700, 5 for $1000 Bundle"
      if (name === "3 for $700, 5 for $1000 Bundle") {
        return;
      }
      const formatted = formatItem(item, 'promotion', promotionData.length);
      promotionData.push(formatted);
    });
  }

  // Format discount panels (same as VA Portal's processList for discountPanel)
  const discountData = [];
  if (discountPanels && Array.isArray(discountPanels)) {
    discountPanels.forEach((item) => {
      // Skip offline panels
      if (item.toBeOffline === true) {
        return;
      }
      // Name handling: add " DBS" suffix for at_home panels (same as VA Portal)
      let name = item.bundleName || item.name || '';
      if (item.section === 'at_home' && !name.toLowerCase().includes('dbs')) {
        name = name + ' DBS';
      }
      const formatted = formatItem(
        { ...item, id: item.bundleId, name, price: item.price || 0 },
        'discount',
        discountData.length
      );
      discountData.push(formatted);
    });
  }

  // Format custom panels (Portal: parseFloat(node.price))
  const customData = [];
  if (customPanels && Array.isArray(customPanels)) {
    customPanels.forEach((item) => {
      const formatted = formatItem({ ...item, price: parseFloat(item.price) || 0 }, 'custom', customData.length);
      customData.push(formatted);
    });
  }

  return { vaData, vwData, promotionData, discountData, customData };
}

/**
 * Format merchandise data from API for table display
 * @param {Array} merchandiseData - raw data from getAllMerchandisesForOrder API
 * @param {Array} markupSchemes - markup schemes from getMarkUps API
 * @returns {Array} formatted data for table
 */
export function formatMerchandiseData(merchandiseData, markupSchemes = []) {
  if (!merchandiseData || !Array.isArray(merchandiseData)) {
    return [];
  }

  // Create a map for quick lookup of markup schemes
  const schemeMap = {};
  if (markupSchemes && Array.isArray(markupSchemes)) {
    markupSchemes.forEach(scheme => {
      const key = `${scheme.item_type}-${scheme.item_id}`;
      schemeMap[key] = scheme;
    });
  }

  return merchandiseData.map((item, index) => {
    // Determine markup scheme
    const schemeKey = `${item.item_type || 'packagePriceId'}-${item.item_id || item.id}`;
    const scheme = schemeMap[schemeKey] || {};

    const markUpType = scheme.markup_type || 'no_markup';
    const markUpValue = scheme.markup_value || 0;
    const wholeSalePrice = item.price || item.wholeSalePrice || 0;
    const markedUpPrice = calculateMarkedUpPrice(wholeSalePrice, markUpType, markUpValue);
    const profit = markedUpPrice - wholeSalePrice;

    // Format markup display
    let markupDisplay = '+0%';
    if (markUpType === 'added_percentage') {
      markupDisplay = `+${(markUpValue * 100).toFixed(0)}%`;
    } else if (markUpType === 'fixed_profit') {
      markupDisplay = `+$${markUpValue.toFixed(0)}`;
    } else if (markUpType === 'fixed_price') {
      markupDisplay = 'Fixed';
    }

    return {
      id: item.id || item.item_id,
      index,
      name: item.name || item.item_name || '',
      category: item.category || '',
      basePrice: wholeSalePrice,
      markup: markupDisplay,
      retailPrice: markedUpPrice,
      revenue: profit,
      markUpType,
      markUpValue,
      wholeSalePrice,
      markedUpPrice,
      type: item.type || 'merchandise',
      isTest: item.isTest !== false,
      changable: scheme.changeable !== false,
      customized_name: scheme.customized_name || '',
      selected: false
    };
  });
}

/**
 * Filter and sort data for table display
 * @param {Array} data - formatted merchandise data
 * @param {Object} filters - filter options
 * @returns {Array} filtered and sorted data
 */
export function filterAndSortMerchandiseData(data, filters = {}) {
  const {
    searchQuery = '',
    searchOption = 'test',
    activeSubTab = 'all',
    customizedValue = 'all',
    isTestValue = 'all',
    sortByValue = 'default'
  } = filters;

  let filtered = [...data];

  // 1. Filter by search query (test name or category)
  if (searchQuery && searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    const field = searchOption === 'category' ? 'category' : 'name';
    filtered = filtered.filter(item =>
      item[field] && item[field].toLowerCase().includes(query)
    );
  }

  // 2. Filter by customization status
  if (customizedValue && customizedValue !== 'all') {
    filtered = filtered.filter(row => {
      if (customizedValue === 'not_customized') {
        return row.markUpType === 'no_markup';
      } else {
        return row.markUpType !== 'no_markup';
      }
    });
  }

  // 3. Filter by test type
  if (isTestValue !== 'all') {
    const bool = isTestValue === 'test';
    filtered = filtered.filter(row => row.isTest === bool);
  }

  // 4. Filter by sub-tab (type)
  if (activeSubTab !== 'all') {
    filtered = filtered.filter(item => {
      if (activeSubTab === 'va') return item.type === 'va';
      if (activeSubTab === 'vw') return item.type === 'vw';
      if (activeSubTab === 'highlights') return item.type === 'promotion';
      if (activeSubTab === 'custom') return item.type === 'custom';
      return true;
    });
  }

  // 5. Sort data
  const typeOrder = ['va', 'vw', 'promotion', 'custom'];

  filtered.sort((a, b) => {
    if (sortByValue === 'nameAZ') {
      return a.name.localeCompare(b.name);
    } else if (sortByValue === 'nameZA') {
      return b.name.localeCompare(a.name);
    } else if (sortByValue === 'profit_descending') {
      return (b.profit || b.revenue || 0) - (a.profit || a.revenue || 0);
    } else if (sortByValue === 'profit_ascending') {
      return (a.profit || a.revenue || 0) - (b.profit || b.revenue || 0);
    } else if (sortByValue === 'default') {
      // Sort by type order first, then by index
      const typeComparison = typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type);
      if (typeComparison !== 0) {
        return typeComparison;
      }
      return (a.index ?? 0) - (b.index ?? 0);
    }
    return 0;
  });

  return filtered;
}
